import { createContext, useContext, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useGeneral } from '../../Context/GeneralContext';
import { useCanEditCapacidades } from '../../usePermisos';
import { useProductos } from './ProductosContext';
import { capLineaDefaultFields, toApi, toDisplay, flattenCeldas, celdaConteoDeLinea } from '../../utils/capacidades';

const ProductosLineasContext = createContext();

const CELL_KEYS = ['piezasPorPaquete', 'ritmoIdeal', 'ritmoEstandar'];

// Mapea un CapacidadDTO (GET /capacidades) a los valores de fila. A diferencia de
// CapacidadesDefault, este GET sí expone cantPorPieza y paquetesPorTarima.
function capValoresFromCap(cap) {
    return {
        ritmoIdeal: toDisplay(cap.ritmoIdeal),
        ritmoEstandar: toDisplay(cap.ritmoEstandar),
        piezasPorPaquete: cap.piezasPorPaquete ?? 0,
        cantPorPieza: cap.cantPorPieza ?? 1,
        paquetesPorTarima: cap.paquetesPorTarima ?? null,
    };
}

async function parseErrorMessage(res) {
    try {
        const data = await res.clone().json();
        if (typeof data === 'string' && data) return data;
        if (data && typeof data === 'object' && data.message) return data.message;
    } catch { /* cuerpo no es JSON */ }
    try {
        const text = await res.text();
        return text || null;
    } catch {
        return null;
    }
}

// Cruza las celdas productivas (CeldaPorLinea) con las capacidades del producto
// (esDeLinea=false, por celda). Sin registro propio → hereda la capacidad de línea.
// `celdaConteo` (celda0Conteo) = celda fuente de la que el back deriva la celda cero.
function buildCeldas(rawCeldas, lineId, capLineaValores, capBySku, celdaConteo) {
    return rawCeldas.map((c) => {
        const cap = capBySku.find(
            (x) => Number(x.linea) === Number(lineId) && Number(x.celda) === Number(c.id) && !x.esDeLinea
        );
        const valores = cap ? capValoresFromCap(cap) : { ...capLineaValores };
        const configurada = !!cap
            && CELL_KEYS.some((k) => Number(valores[k]) !== Number(capLineaValores[k]));
        return {
            celdaId: c.id,
            nombre: c.nombre,
            configurada,
            valores,
            original: { ...valores },
            saving: false,
            esFuente: celdaConteo != null && Number(c.id) === Number(celdaConteo),
        };
    });
}

export function ProductosLineasProvider(props) {
    const { tokenR } = useGeneral();
    const canEdit = useCanEditCapacidades();
    const { selectedIndex, isProdSelected, prodName, setMsgSb, setSeveritySb, setSnackbar } = useProductos();

    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState(null);
    const [lines, setLines] = useState([]);
    const [enabledLines, setEnabledLines] = useState([]);
    const [capBySku, setCapBySku] = useState([]); // todas las capacidades (celda cero + reales) del producto
    const [lineData, setLineData] = useState({});
    const [togglingLine, setTogglingLine] = useState(null);

    const controllersRef = useRef({});

    const notify = useCallback((msg, severity = 'success') => {
        setMsgSb(msg);
        setSeveritySb(severity);
        setSnackbar(true);
    }, [setMsgSb, setSeveritySb, setSnackbar]);

    const authHeaders = useCallback(
        () => ({ authorization: `Bearer ${tokenR.current}`, Accept: 'application/json' }),
        [tokenR]
    );

    const getAreas = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/areas`, {
                method: 'GET', headers: authHeaders(),
            });
            if (res.ok) setAreas(await res.json());
        } catch { /* ignore */ }
    }, [authHeaders]);

    const selectArea = useCallback(async (area) => {
        setSelectedArea(area);
        const url = area
            ? `${process.env.REACT_APP_SERVICE_ENDPOINT}/lineas?area=${area.area1}`
            : `${process.env.REACT_APP_SERVICE_ENDPOINT}/lineas`;
        try {
            const res = await fetch(url, { method: 'GET', headers: authHeaders() });
            setLines(res.ok ? await res.json() : []);
        } catch {
            setLines([]);
        }
    }, [authHeaders]);

    // Carga elegibilidad (/productolinea) y capacidades (/capacidades) del producto.
    const loadProducto = useCallback(async (sku) => {
        setLineData({});
        setEnabledLines([]);
        setCapBySku([]);
        if (!sku) return;
        try {
            const [eligRes, capRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/productolinea/${sku}`, { method: 'GET', headers: authHeaders() }),
                fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/capacidades?sku=${sku}`, { method: 'GET', headers: authHeaders() }),
            ]);
            if (eligRes.ok) {
                const rows = await eligRes.json();
                setEnabledLines(Array.isArray(rows) ? rows.map((r) => r.linea) : []);
            }
            // 404 = el producto aún no tiene capacidades; no es error.
            if (capRes.ok) {
                const data = await capRes.json();
                setCapBySku(Array.isArray(data) ? data : []);
            }
        } catch { /* ignore */ }
    }, [authHeaders]);

    useEffect(() => {
        getAreas();
    }, [getAreas]);

    // Autoseleccionar la primera área al cargar.
    useEffect(() => {
        if (areas.length > 0 && !selectedArea) selectArea(areas[0]);
    }, [areas, selectedArea, selectArea]);

    useEffect(() => {
        if (!isProdSelected && selectedIndex) loadProducto(selectedIndex);
        else { setLineData({}); setEnabledLines([]); setCapBySku([]); }
    }, [selectedIndex, isProdSelected, loadProducto]);

    // Capacidad de la línea = celda cero (esDeLinea=true) del producto en esa línea.
    const capForLine = useCallback((lineId) => {
        const cero = capBySku.find((c) => Number(c.linea) === Number(lineId) && c.esDeLinea === true);
        if (!cero) {
            return {
                valores: { ritmoIdeal: 0, ritmoEstandar: 0, piezasPorPaquete: 0, cantPorPieza: 1, paquetesPorTarima: null },
                existe: false,
            };
        }
        return { valores: capValoresFromCap(cero), existe: true };
    }, [capBySku]);

    // ── Toggle elegibilidad línea para el producto (/productolinea) ──
    const toggleLinea = useCallback(async (lineId, habilitar) => {
        if (!selectedIndex) return;
        setTogglingLine(lineId);
        setEnabledLines((prev) => (habilitar ? [...prev, lineId] : prev.filter((l) => l !== lineId)));
        try {
            const url = habilitar
                ? `${process.env.REACT_APP_SERVICE_ENDPOINT}/productolinea`
                : `${process.env.REACT_APP_SERVICE_ENDPOINT}/productolinea/eliminar`;
            const res = await fetch(url, {
                method: habilitar ? 'POST' : 'PUT',
                body: JSON.stringify({ linea: lineId, sku: selectedIndex }),
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            });
            if (![200, 201, 204].includes(res.status)) throw new Error('toggle failed');
            notify(habilitar ? 'Línea habilitada para el producto' : 'Línea inhabilitada para el producto');
        } catch {
            setEnabledLines((prev) => (habilitar ? prev.filter((l) => l !== lineId) : [...prev, lineId]));
            notify('No se pudo actualizar la línea del producto', 'error');
        } finally {
            setTogglingLine(null);
        }
    }, [selectedIndex, authHeaders, notify]);

    // ── Acordeón de líneas ──
    const loadLinea = useCallback(async (lineId) => {
        controllersRef.current[lineId]?.abort();
        const controller = new AbortController();
        controllersRef.current[lineId] = controller;

        setLineData((prev) => ({ ...prev, [lineId]: { ...prev[lineId], loading: true } }));
        try {
            const capLineaInfo = capForLine(lineId);
            const celdasRes = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/CeldaPorLinea/CeldaPorLinea?linea=${lineId}`,
                { method: 'GET', headers: authHeaders(), signal: controller.signal }
            );
            const celdasJson = celdasRes.ok ? await celdasRes.json() : [];
            const rawCeldas = flattenCeldas(celdasJson);
            const celdaConteo = celdaConteoDeLinea(celdasJson);
            const celdas = buildCeldas(rawCeldas, lineId, capLineaInfo.valores, capBySku, celdaConteo);
            setLineData((prev) => ({
                ...prev,
                [lineId]: {
                    ...prev[lineId],
                    loading: false,
                    loaded: true,
                    // Se cachea la respuesta de CeldaPorLinea para poder reconstruir la
                    // línea tras guardar sin volver a pedirla.
                    celdasJson,
                    capLinea: {
                        valores: capLineaInfo.valores,
                        original: { ...capLineaInfo.valores },
                        existe: capLineaInfo.existe,
                        // Siempre campos completos: en modo carga inicial se capturan todos.
                        fields: capLineaDefaultFields(true),
                    },
                    celdas,
                    lineaSaving: false,
                },
            }));
        } catch (err) {
            if (err.name === 'AbortError') return;
            setLineData((prev) => ({ ...prev, [lineId]: { ...prev[lineId], loading: false, loaded: true, capLinea: null, celdas: [] } }));
            notify('No se pudieron cargar las celdas de la línea', 'error');
        }
    }, [authHeaders, capForLine, capBySku, notify]);

    const toggleExpand = useCallback((lineId) => {
        setLineData((prev) => {
            const cur = prev[lineId] || {};
            const nextExpanded = !cur.expanded;
            if (nextExpanded && !cur.loaded && !cur.loading) {
                Promise.resolve().then(() => loadLinea(lineId));
            }
            return { ...prev, [lineId]: { ...cur, expanded: nextExpanded } };
        });
    }, [loadLinea]);

    // ── Capacidad por celda (sku, línea, celda) — PUT /capacidades (array, upsert) ──
    // El front NUNCA envía la celda cero; el back la deriva de la celda de conteo.
    const setCeldaField = useCallback((lineId, celdaId, key, value) => {
        setLineData((prev) => {
            const ld = prev[lineId];
            if (!ld) return prev;
            const celdas = ld.celdas.map((c) => (c.celdaId === celdaId ? { ...c, valores: { ...c.valores, [key]: value } } : c));
            return { ...prev, [lineId]: { ...ld, celdas } };
        });
    }, []);

    const putCelda = useCallback(async (lineId, celdaId, valores) => {
        const body = [{
            linea: lineId,
            sku: selectedIndex,
            celda: celdaId,
            ritmoIdeal: toApi(valores.ritmoIdeal),
            ritmoEstandar: toApi(valores.ritmoEstandar),
            cantPorPieza: valores.cantPorPieza || 1,
            piezasPorPaquete: valores.piezasPorPaquete,
            paquetesPorTarima: valores.paquetesPorTarima == null || valores.paquetesPorTarima === '' ? null : valores.paquetesPorTarima,
        }];
        const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/capacidades`, {
            method: 'PUT',
            body: JSON.stringify(body),
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        });
        if (![200, 201, 204].includes(res.status)) {
            if (res.status === 403) throw new Error('No tienes permisos para modificar esta capacidad');
            if (res.status === 404) throw new Error('No existe la línea o el producto');
            throw new Error((await parseErrorMessage(res)) || 'No se pudo guardar la capacidad de celda');
        }
    }, [selectedIndex, authHeaders]);

    // Reconstruye una línea a partir de los datos del servicio (capArr) + las celdas
    // cacheadas. Fuente única de verdad: refleja lo que el back devolvió, sin propagar
    // cambios de forma optimista (p. ej. al editar la celda fuente, la celda cero se
    // toma del back, no se calcula en el front).
    const reconstruirLinea = useCallback((lineId, capArr, prevLine) => {
        const celdasJson = prevLine?.celdasJson || [];
        const rawCeldas = flattenCeldas(celdasJson);
        const celdaConteo = celdaConteoDeLinea(celdasJson);
        const cero = capArr.find((c) => Number(c.linea) === Number(lineId) && c.esDeLinea === true);
        const capLineaValores = cero
            ? capValoresFromCap(cero)
            : { ritmoIdeal: 0, ritmoEstandar: 0, piezasPorPaquete: 0, cantPorPieza: 1, paquetesPorTarima: null };
        const celdas = buildCeldas(rawCeldas, lineId, capLineaValores, capArr, celdaConteo);
        return {
            ...prevLine,
            capLinea: {
                valores: capLineaValores,
                original: { ...capLineaValores },
                existe: !!cero,
                fields: capLineaDefaultFields(true),
            },
            celdas,
            lineaSaving: false,
        };
    }, []);

    // Tras escribir, re-consulta el mismo GET que llena el componente y reconstruye la
    // línea con la respuesta del back.
    const refrescarCapacidades = useCallback(async (lineId) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/capacidades?sku=${selectedIndex}`,
                { method: 'GET', headers: authHeaders() }
            );
            const data = res.ok ? await res.json() : [];
            const capArr = Array.isArray(data) ? data : [];
            setCapBySku(capArr);
            setLineData((prev) => {
                const cur = prev[lineId];
                if (!cur) return prev;
                return { ...prev, [lineId]: reconstruirLinea(lineId, capArr, cur) };
            });
        } catch { /* ignore */ }
    }, [selectedIndex, authHeaders, reconstruirLinea]);

    const guardarCapCelda = useCallback(async (lineId, celdaId) => {
        const ld = lineData[lineId];
        const celda = ld?.celdas.find((c) => c.celdaId === celdaId);
        if (!ld || !celda) return;
        const valores = { ...celda.valores };

        setLineData((prev) => {
            const cur = prev[lineId];
            const celdas = cur.celdas.map((c) => (c.celdaId === celdaId ? { ...c, saving: true } : c));
            return { ...prev, [lineId]: { ...cur, celdas } };
        });
        try {
            await putCelda(lineId, celdaId, valores);
            // La celda cero (y cualquier derivado) se toma del servicio, no del front.
            await refrescarCapacidades(lineId);
            notify('Capacidad de celda guardada');
        } catch (err) {
            setLineData((prev) => {
                const cur = prev[lineId];
                const celdas = cur.celdas.map((c) => (c.celdaId === celdaId ? { ...c, saving: false } : c));
                return { ...prev, [lineId]: { ...cur, celdas } };
            });
            notify(err.message || 'No se pudo guardar la capacidad de celda', 'error');
        }
    }, [lineData, putCelda, refrescarCapacidades, notify]);

    // Restaurar al default de línea = persistir en la celda los valores de la celda cero.
    const resetCapCelda = useCallback(async (lineId, celdaId) => {
        const ld = lineData[lineId];
        const celda = ld?.celdas.find((c) => c.celdaId === celdaId);
        if (!ld?.capLinea || !celda) return;
        const base = ld.capLinea.valores;
        const valores = {
            ritmoIdeal: base.ritmoIdeal,
            ritmoEstandar: base.ritmoEstandar,
            piezasPorPaquete: base.piezasPorPaquete,
            cantPorPieza: base.cantPorPieza ?? 1,
            paquetesPorTarima: base.paquetesPorTarima ?? null,
        };

        setLineData((prev) => {
            const cur = prev[lineId];
            const celdas = cur.celdas.map((c) => (c.celdaId === celdaId ? { ...c, saving: true } : c));
            return { ...prev, [lineId]: { ...cur, celdas } };
        });
        try {
            await putCelda(lineId, celdaId, valores);
            await refrescarCapacidades(lineId);
            notify('Celda restaurada al default de línea', 'info');
        } catch (err) {
            setLineData((prev) => {
                const cur = prev[lineId];
                const celdas = cur.celdas.map((c) => (c.celdaId === celdaId ? { ...c, saving: false } : c));
                return { ...prev, [lineId]: { ...cur, celdas } };
            });
            notify(err.message || 'No se pudo restaurar la celda', 'error');
        }
    }, [lineData, putCelda, refrescarCapacidades, notify]);

    // ── Carga inicial de una línea+SKU — POST /capacidades (array de TODAS las celdas) ──
    // Solo cuando la línea aún no tiene capacidad (el POST devuelve 409 si ya existe).
    const crearCapacidadLinea = useCallback(async (lineId) => {
        const ld = lineData[lineId];
        if (!ld?.celdas?.length) return;

        // El contrato exige ritmos>0 y piezasPorPaquete>0 en TODAS las celdas.
        const incompleta = ld.celdas.some((c) => {
            const v = c.valores;
            return !(toApi(v.ritmoIdeal) > 0) || !(toApi(v.ritmoEstandar) > 0) || !(v.piezasPorPaquete > 0);
        });
        if (incompleta) {
            notify('Completa ritmos (> 0) y piezas por paquete (> 0) en todas las celdas', 'error');
            return;
        }

        const body = ld.celdas.map((c) => ({
            linea: lineId,
            sku: selectedIndex,
            celda: c.celdaId,
            ritmoIdeal: toApi(c.valores.ritmoIdeal),
            ritmoEstandar: toApi(c.valores.ritmoEstandar),
            cantPorPieza: c.valores.cantPorPieza || 1,
            piezasPorPaquete: c.valores.piezasPorPaquete,
            paquetesPorTarima: c.valores.paquetesPorTarima == null || c.valores.paquetesPorTarima === '' ? null : c.valores.paquetesPorTarima,
        }));

        setLineData((prev) => ({ ...prev, [lineId]: { ...prev[lineId], lineaSaving: true } }));
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/capacidades`, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            });
            if (![200, 201, 204].includes(res.status)) {
                if (res.status === 409) throw new Error('Ya existe capacidad para esta línea; recarga el producto');
                if (res.status === 404) throw new Error('No existe la línea o el producto');
                throw new Error((await parseErrorMessage(res)) || 'No se pudo crear la capacidad de la línea');
            }
            // Reconstruye la línea con lo que el back guardó (incluida la celda cero).
            await refrescarCapacidades(lineId);
            notify('Capacidad de la línea creada');
        } catch (err) {
            setLineData((prev) => ({ ...prev, [lineId]: { ...prev[lineId], lineaSaving: false } }));
            notify(err.message || 'No se pudo crear la capacidad de la línea', 'error');
        }
    }, [lineData, selectedIndex, authHeaders, refrescarCapacidades, notify]);

    const values = useMemo(() => ({
        canEdit,
        prodName,
        isProdSelected,
        selectedIndex,
        areas,
        selectedArea,
        lines,
        enabledLines,
        lineData,
        togglingLine,
        selectArea,
        toggleLinea,
        toggleExpand,
        setCeldaField,
        guardarCapCelda,
        resetCapCelda,
        crearCapacidadLinea,
    }), [
        canEdit, prodName, isProdSelected, selectedIndex, areas, selectedArea, lines, enabledLines,
        lineData, togglingLine, selectArea, toggleLinea, toggleExpand,
        setCeldaField, guardarCapCelda, resetCapCelda, crearCapacidadLinea,
    ]);

    return <ProductosLineasContext.Provider value={values} {...props} />;
}

export function useProductosLineas() {
    const context = useContext(ProductosLineasContext);
    if (!context) throw new Error('useProductosLineas debe estar dentro de ProductosLineasProvider');
    return context;
}
